import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Button, Slider, Select, Typography, Tooltip, Spin, Input } from 'antd';
import apiClient from '../../../api/client';

const { Text } = Typography;

/**
 * DitherEditorPanel — Dithering effect editor for the Scrapbook.
 * Supports Floyd-Steinberg error diffusion, Bayer ordered dithering,
 * and simple quantization with configurable palettes.
 *
 * Props:
 *  - visible, onClose, onApply(dataUrl), sourceImageUrl, proxyUrl
 */
const DitherEditorPanel = ({ visible, onClose, onApply, sourceImageUrl, proxyUrl, canvasId }) => {
    const previewCanvasRef = useRef(null);
    const sourceImgRef = useRef(null);
    const processingRef = useRef(false);
    const pendingRef = useRef(false);
    const workerRef = useRef(null);
    const generationRef = useRef(0);

    // Settings
    const [algorithm, setAlgorithm] = useState('floyd-steinberg');
    const [colorLevels, setColorLevels] = useState(4);
    const [paletteMode, setPaletteMode] = useState('auto'); // auto, bw, custom
    const [customColors, setCustomColors] = useState(['#000000', '#ffffff', '#ff0000', '#0000ff']);
    const [brightness, setBrightness] = useState(0);
    const [contrast, setContrast] = useState(1.0);
    const [pixelScale, setPixelScale] = useState(1);

    const [loading, setLoading] = useState(false);

    // User palettes
    const [userPalettes, setUserPalettes] = useState([]);
    useEffect(() => {
        if (!visible) return;
        const fetchPalettes = async () => {
            try {
                const response = await apiClient.get('/color-palettes', { params: canvasId ? { canvasId } : {} });
                setUserPalettes(response.data || []);
            } catch (e) { /* silent */ }
        };
        fetchPalettes();
    }, [visible, canvasId]);

    const applyPalette = (palette) => {
        const colors = (palette.colors || []).map(c => typeof c === 'string' ? c : c.color).filter(Boolean);
        if (colors.length > 0) {
            setCustomColors(colors.slice(0, 8));
            setPaletteMode('custom');
        }
    };

    // Load source image
    useEffect(() => {
        if (!visible || !sourceImageUrl) return;
        setLoading(true);
        const img = new Image();
        img.crossOrigin = 'anonymous';
        img.onload = () => { sourceImgRef.current = img; setLoading(false); };
        img.onerror = () => { setLoading(false); };
        img.src = proxyUrl || sourceImageUrl;
    }, [visible, sourceImageUrl, proxyUrl]);

    // Effects worker lifecycle
    useEffect(() => {
        try {
            workerRef.current = new Worker(
                new URL('../workers/effectsWorker.js', import.meta.url)
            );
        } catch (err) {
            console.warn('Failed to create effects worker, falling back to main thread:', err);
        }
        return () => {
            if (workerRef.current) {
                workerRef.current.terminate();
                workerRef.current = null;
            }
        };
    }, []);

    // Re-render on changes
    useEffect(() => {
        if (!visible || !sourceImgRef.current) return;
        renderDither();
    }, [visible, algorithm, colorLevels, paletteMode, customColors, brightness, contrast, pixelScale, loading]);

    const renderDither = useCallback(() => {
        if (!sourceImgRef.current || !previewCanvasRef.current) return;
        if (processingRef.current) { pendingRef.current = true; return; }
        processingRef.current = true;

        const worker = workerRef.current;
        const img = sourceImgRef.current;
        const maxDim = 800;
        const scale = Math.min(1, maxDim / Math.max(img.width, img.height));
        const ps = Math.max(1, Math.round(pixelScale));
        const w = Math.round(img.width * scale / ps);
        const h = Math.round(img.height * scale / ps);

        if (worker) {
            const generation = ++generationRef.current;
            const tmpCanvas = document.createElement('canvas');
            tmpCanvas.width = w; tmpCanvas.height = h;
            const tmpCtx = tmpCanvas.getContext('2d');
            tmpCtx.imageSmoothingEnabled = false;
            tmpCtx.drawImage(img, 0, 0, w, h);
            const imgData = tmpCtx.getImageData(0, 0, w, h);
            const srcBuf = imgData.data.buffer.slice(0);
            const alphaBuf = imgData.data.buffer.slice(0);

            worker.onmessage = (ev) => {
                if (ev.data.generation !== generationRef.current) return;
                const result = new Uint8ClampedArray(ev.data.resultPixels);
                const preview = previewCanvasRef.current;
                if (preview) {
                    const outW = ev.data.width * ps, outH = ev.data.height * ps;
                    preview.width = outW; preview.height = outH;
                    const pCtx = preview.getContext('2d');
                    pCtx.imageSmoothingEnabled = false;
                    const workC = document.createElement('canvas');
                    workC.width = ev.data.width; workC.height = ev.data.height;
                    workC.getContext('2d').putImageData(new ImageData(result, ev.data.width, ev.data.height), 0, 0);
                    pCtx.drawImage(workC, 0, 0, outW, outH);
                }
                processingRef.current = false;
                if (pendingRef.current) { pendingRef.current = false; renderDither(); }
            };

            worker.postMessage({
                type: 'dither', pixels: srcBuf, alphaPixels: alphaBuf,
                width: w, height: h, algorithm, colorLevels, paletteMode,
                customColors, brightness, contrast, generation,
            }, [srcBuf, alphaBuf]);
        } else {
            requestAnimationFrame(() => {
                try { doRender(previewCanvasRef.current, 800); }
                finally {
                    processingRef.current = false;
                    if (pendingRef.current) { pendingRef.current = false; renderDither(); }
                }
            });
        }
    }, [algorithm, colorLevels, paletteMode, customColors, brightness, contrast, pixelScale]);

    const doRender = (canvas, maxDim) => {
        const img = sourceImgRef.current;
        if (!img || !canvas) return;

        const scale = Math.min(1, maxDim / Math.max(img.width, img.height));
        const ps = Math.max(1, Math.round(pixelScale));
        let w = Math.round(img.width * scale / ps);
        let h = Math.round(img.height * scale / ps);

        // Work canvas at reduced res
        const workCanvas = document.createElement('canvas');
        workCanvas.width = w;
        workCanvas.height = h;
        const workCtx = workCanvas.getContext('2d');
        workCtx.imageSmoothingEnabled = false;
        workCtx.drawImage(img, 0, 0, w, h);

        const imageData = workCtx.getImageData(0, 0, w, h);
        const data = imageData.data;

        // Pre-filter: brightness & contrast
        for (let i = 0; i < data.length; i += 4) {
            for (let c = 0; c < 3; c++) {
                let val = data[i + c] / 255;
                val += brightness;
                val = (val - 0.5) * contrast + 0.5;
                data[i + c] = Math.max(0, Math.min(255, Math.round(val * 255)));
            }
        }

        // Build palette
        const palette = buildPalette();

        // Apply dithering
        if (algorithm === 'floyd-steinberg') {
            floydSteinberg(data, w, h, palette);
        } else if (algorithm.startsWith('bayer')) {
            const bayerSize = algorithm === 'bayer2' ? 2 : algorithm === 'bayer4' ? 4 : 8;
            bayerDither(data, w, h, palette, bayerSize);
        } else {
            // Simple quantize
            quantize(data, w, h, palette);
        }

        workCtx.putImageData(imageData, 0, 0);

        // Alpha mask at work resolution
        const alphaCanvas = document.createElement('canvas');
        alphaCanvas.width = w;
        alphaCanvas.height = h;
        const alphaCtx = alphaCanvas.getContext('2d');
        alphaCtx.imageSmoothingEnabled = false;
        alphaCtx.drawImage(img, 0, 0, w, h);
        const alphaData = alphaCtx.getImageData(0, 0, w, h);
        const resultData = workCtx.getImageData(0, 0, w, h);
        for (let i = 0; i < resultData.data.length; i += 4) {
            resultData.data[i + 3] = alphaData.data[i + 3];
        }
        workCtx.putImageData(resultData, 0, 0);

        // Scale up to output
        const outW = w * ps;
        const outH = h * ps;
        canvas.width = outW;
        canvas.height = outH;
        const ctx = canvas.getContext('2d');
        ctx.imageSmoothingEnabled = false;
        ctx.drawImage(workCanvas, 0, 0, outW, outH);
    };

    const buildPalette = () => {
        if (paletteMode === 'bw') {
            return [[0, 0, 0], [255, 255, 255]];
        }
        if (paletteMode === 'custom') {
            return customColors.map(hex => {
                const h = hex.replace('#', '');
                return [parseInt(h.substring(0, 2), 16) || 0, parseInt(h.substring(2, 4), 16) || 0, parseInt(h.substring(4, 6), 16) || 0];
            });
        }
        // Auto: evenly spaced levels per channel
        const levels = colorLevels;
        const palette = [];
        for (let r = 0; r < levels; r++) {
            for (let g = 0; g < levels; g++) {
                for (let b = 0; b < levels; b++) {
                    palette.push([
                        Math.round(r * 255 / (levels - 1)),
                        Math.round(g * 255 / (levels - 1)),
                        Math.round(b * 255 / (levels - 1)),
                    ]);
                }
            }
        }
        return palette;
    };

    const findClosest = (r, g, b, palette) => {
        let minDist = Infinity, best = palette[0];
        for (const c of palette) {
            const dr = r - c[0], dg = g - c[1], db = b - c[2];
            const dist = dr * dr + dg * dg + db * db;
            if (dist < minDist) { minDist = dist; best = c; }
        }
        return best;
    };

    const floydSteinberg = (data, w, h, palette) => {
        for (let y = 0; y < h; y++) {
            for (let x = 0; x < w; x++) {
                const idx = (y * w + x) * 4;
                const oldR = data[idx], oldG = data[idx + 1], oldB = data[idx + 2];
                const [newR, newG, newB] = findClosest(oldR, oldG, oldB, palette);
                data[idx] = newR; data[idx + 1] = newG; data[idx + 2] = newB;

                const errR = oldR - newR, errG = oldG - newG, errB = oldB - newB;
                const spread = [
                    [x + 1, y, 7 / 16],
                    [x - 1, y + 1, 3 / 16],
                    [x, y + 1, 5 / 16],
                    [x + 1, y + 1, 1 / 16],
                ];
                for (const [sx, sy, factor] of spread) {
                    if (sx >= 0 && sx < w && sy < h) {
                        const si = (sy * w + sx) * 4;
                        data[si] = Math.max(0, Math.min(255, data[si] + errR * factor));
                        data[si + 1] = Math.max(0, Math.min(255, data[si + 1] + errG * factor));
                        data[si + 2] = Math.max(0, Math.min(255, data[si + 2] + errB * factor));
                    }
                }
            }
        }
    };

    const bayerMatrix = (size) => {
        if (size === 2) return [[0, 2], [3, 1]];
        if (size === 4) return [
            [0, 8, 2, 10], [12, 4, 14, 6],
            [3, 11, 1, 9], [15, 7, 13, 5],
        ];
        return [
            [0, 32, 8, 40, 2, 34, 10, 42],
            [48, 16, 56, 24, 50, 18, 58, 26],
            [12, 44, 4, 36, 14, 46, 6, 38],
            [60, 28, 52, 20, 62, 30, 54, 22],
            [3, 35, 11, 43, 1, 33, 9, 41],
            [51, 19, 59, 27, 49, 17, 57, 25],
            [15, 47, 7, 39, 13, 45, 5, 37],
            [63, 31, 55, 23, 61, 29, 53, 21],
        ];
    };

    const bayerDither = (data, w, h, palette, size) => {
        const matrix = bayerMatrix(size);
        const n = size * size;
        for (let y = 0; y < h; y++) {
            for (let x = 0; x < w; x++) {
                const idx = (y * w + x) * 4;
                const threshold = (matrix[y % size][x % size] / n - 0.5) * (256 / colorLevels);
                const r = Math.max(0, Math.min(255, data[idx] + threshold));
                const g = Math.max(0, Math.min(255, data[idx + 1] + threshold));
                const b = Math.max(0, Math.min(255, data[idx + 2] + threshold));
                const [newR, newG, newB] = findClosest(r, g, b, palette);
                data[idx] = newR; data[idx + 1] = newG; data[idx + 2] = newB;
            }
        }
    };

    const quantize = (data, w, h, palette) => {
        for (let i = 0; i < data.length; i += 4) {
            const [r, g, b] = findClosest(data[i], data[i + 1], data[i + 2], palette);
            data[i] = r; data[i + 1] = g; data[i + 2] = b;
        }
    };

    // Apply at full res
    const handleApply = useCallback(() => {
        const img = sourceImgRef.current;
        if (!img) return;
        const worker = workerRef.current;
        const ps = Math.max(1, Math.round(pixelScale));
        const w = Math.round(img.width / ps);
        const h = Math.round(img.height / ps);

        if (worker) {
            const generation = ++generationRef.current;
            const tmpCanvas = document.createElement('canvas');
            tmpCanvas.width = w; tmpCanvas.height = h;
            const tmpCtx = tmpCanvas.getContext('2d');
            tmpCtx.imageSmoothingEnabled = false;
            tmpCtx.drawImage(img, 0, 0, w, h);
            const imgData = tmpCtx.getImageData(0, 0, w, h);
            const srcBuf = imgData.data.buffer.slice(0);
            const alphaBuf = imgData.data.buffer.slice(0);

            worker.onmessage = (ev) => {
                if (ev.data.generation !== generationRef.current) return;
                const result = new Uint8ClampedArray(ev.data.resultPixels);
                const outW = ev.data.width * ps, outH = ev.data.height * ps;
                const outCanvas = document.createElement('canvas');
                outCanvas.width = outW; outCanvas.height = outH;
                const outCtx = outCanvas.getContext('2d');
                outCtx.imageSmoothingEnabled = false;
                const workC = document.createElement('canvas');
                workC.width = ev.data.width; workC.height = ev.data.height;
                workC.getContext('2d').putImageData(new ImageData(result, ev.data.width, ev.data.height), 0, 0);
                outCtx.drawImage(workC, 0, 0, outW, outH);
                onApply?.(outCanvas.toDataURL('image/png'));
            };

            worker.postMessage({
                type: 'dither', pixels: srcBuf, alphaPixels: alphaBuf,
                width: w, height: h, algorithm, colorLevels, paletteMode,
                customColors, brightness, contrast, generation,
            }, [srcBuf, alphaBuf]);
        } else {
            const fullCanvas = document.createElement('canvas');
            doRender(fullCanvas, Math.max(img.width, img.height));
            onApply?.(fullCanvas.toDataURL('image/png'));
        }
    }, [algorithm, colorLevels, paletteMode, customColors, brightness, contrast, pixelScale, onApply]);

    // UI helpers
    const sliderRow = (label, min, max, value, onChange, step = 1) => (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
            <Text style={{ color: '#8b949e', fontSize: 11, minWidth: 80, whiteSpace: 'nowrap' }}>{label}</Text>
            <Slider min={min} max={max} step={step} value={value} onChange={onChange}
                style={{ flex: 1, margin: 0 }}
                styles={{ track: { background: '#34d399' }, rail: { background: '#30363d' }, handle: { borderColor: '#34d399', width: 12, height: 12 } }}
            />
            <Text style={{ color: '#34d399', fontSize: 11, fontFamily: 'monospace', minWidth: 36, textAlign: 'right' }}>
                {step < 1 ? value.toFixed(2) : value}
            </Text>
        </div>
    );

    if (!visible) return null;

    return (
        <div style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, zIndex: 110, display: 'flex', background: 'rgba(13, 17, 23, 0.95)', pointerEvents: 'auto' }}
            onClick={(e) => e.stopPropagation()}>
            {/* Preview */}
            <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24, overflow: 'hidden' }}>
                {loading ? (
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 12 }}>
                        <Spin size="large" /><Text style={{ color: '#8b949e' }}>Loading image...</Text>
                    </div>
                ) : (
                    <canvas ref={previewCanvasRef} style={{ maxWidth: '100%', maxHeight: '100%', borderRadius: 8, boxShadow: '0 8px 40px rgba(0,0,0,0.6)', border: '1px solid #30363d', imageRendering: pixelScale > 1 ? 'pixelated' : 'auto' }} />
                )}
            </div>

            {/* Controls */}
            <div style={{ width: 310, flexShrink: 0, borderLeft: '1px solid #30363d', display: 'flex', flexDirection: 'column', background: '#0d1117' }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 14px', borderBottom: '1px solid #30363d', background: '#161b22', flexShrink: 0 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <span style={{ fontSize: 16 }}>▦</span>
                        <span style={{ color: '#c9d1d9', fontWeight: 600, fontSize: 14 }}>Dither</span>
                    </div>
                    <Button type="text" size="small" onClick={onClose} style={{ color: '#8b949e', fontSize: 12, padding: '2px 6px' }}>✕</Button>
                </div>

                <div style={{ flex: 1, overflowY: 'auto', padding: '12px 14px', display: 'flex', flexDirection: 'column', gap: 16 }}>
                    {/* Algorithm */}
                    <div>
                        <Text style={{ color: '#34d399', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5 }}>Algorithm</Text>
                        <Select value={algorithm} onChange={setAlgorithm} style={{ width: '100%', marginTop: 6 }}
                            options={[
                                { value: 'floyd-steinberg', label: 'Floyd-Steinberg' },
                                { value: 'bayer2', label: 'Bayer 2×2' },
                                { value: 'bayer4', label: 'Bayer 4×4' },
                                { value: 'bayer8', label: 'Bayer 8×8' },
                                { value: 'none', label: 'Quantize (no dither)' },
                            ]}
                        />
                    </div>

                    {/* Palette */}
                    <div>
                        <Text style={{ color: '#34d399', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5 }}>Palette</Text>
                        <Select value={paletteMode} onChange={setPaletteMode} style={{ width: '100%', marginTop: 6 }}
                            options={[
                                { value: 'auto', label: 'Auto (RGB Levels)' },
                                { value: 'bw', label: 'Black & White' },
                                { value: 'custom', label: 'Custom Colors' },
                            ]}
                        />
                        {paletteMode === 'auto' && (
                            <div style={{ marginTop: 8 }}>
                                {sliderRow('Color Levels', 2, 8, colorLevels, setColorLevels)}
                            </div>
                        )}
                        {paletteMode === 'custom' && (
                            <div style={{ marginTop: 8, display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                                {customColors.map((c, i) => (
                                    <div key={i} style={{ position: 'relative' }}>
                                        <div
                                            style={{ width: 28, height: 28, borderRadius: 4, background: c, border: '1px solid #30363d', cursor: 'pointer' }}
                                            onClick={() => {
                                                const input = document.createElement('input');
                                                input.type = 'color'; input.value = c;
                                                input.style.cssText = 'position:absolute;opacity:0;pointer-events:none';
                                                document.body.appendChild(input);
                                                input.addEventListener('input', (e) => {
                                                    setCustomColors(prev => { const n = [...prev]; n[i] = e.target.value; return n; });
                                                });
                                                input.addEventListener('change', () => document.body.removeChild(input));
                                                input.click();
                                            }}
                                        />
                                    </div>
                                ))}
                                {customColors.length < 8 && (
                                    <div
                                        style={{ width: 28, height: 28, borderRadius: 4, border: '1px dashed #30363d', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', color: '#6e7681', fontSize: 16 }}
                                        onClick={() => setCustomColors(prev => [...prev, '#888888'])}
                                    >+</div>
                                )}
                            </div>
                        )}

                        {/* User Palettes in Palette section */}
                        {userPalettes.length > 0 && (
                            <div style={{ marginTop: 8 }}>
                                <Text style={{ color: '#6e7681', fontSize: 10, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 }}>Your Palettes</Text>
                                <div style={{ marginTop: 4, display: 'flex', flexDirection: 'column', gap: 4 }}>
                                    {userPalettes.map((palette) => {
                                        const colors = (palette.colors || []).map(c => typeof c === 'string' ? c : c.color).filter(Boolean);
                                        if (colors.length === 0) return null;
                                        return (
                                            <div
                                                key={palette.id || palette.name}
                                                onClick={() => applyPalette(palette)}
                                                style={{
                                                    cursor: 'pointer', padding: '4px 6px', borderRadius: 4,
                                                    border: '1px solid #30363d', background: '#161b22',
                                                    transition: 'all 0.15s ease',
                                                }}
                                                onMouseEnter={(e) => { e.currentTarget.style.borderColor = '#34d399'; e.currentTarget.style.background = '#1c2333'; }}
                                                onMouseLeave={(e) => { e.currentTarget.style.borderColor = '#30363d'; e.currentTarget.style.background = '#161b22'; }}
                                            >
                                                <Text style={{ color: '#c9d1d9', fontSize: 10, display: 'block', marginBottom: 3 }}>{palette.name}</Text>
                                                <div style={{ display: 'flex', gap: 2 }}>
                                                    {colors.map((c, i) => (
                                                        <div key={i} style={{ width: 14, height: 14, borderRadius: 2, background: c, border: '1px solid rgba(255,255,255,0.1)', flexShrink: 0 }} />
                                                    ))}
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Settings */}
                    <div>
                        <Text style={{ color: '#34d399', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5 }}>Settings</Text>
                        <div style={{ marginTop: 8 }}>
                            {sliderRow('Pixel Scale', 1, 8, pixelScale, setPixelScale)}
                            {sliderRow('Brightness', -0.5, 0.5, brightness, setBrightness, 0.01)}
                            {sliderRow('Contrast', 0.5, 2.0, contrast, (v) => setContrast(v), 0.01)}
                        </div>
                    </div>
                </div>

                {/* Actions */}
                <div style={{ padding: '12px 14px', borderTop: '1px solid #30363d', display: 'flex', gap: 8, flexShrink: 0, background: '#161b22' }}>
                    <Button onClick={onClose} style={{ flex: 1, background: '#21262d', borderColor: '#30363d', color: '#c9d1d9' }}>Cancel</Button>
                    <Button type="primary" onClick={handleApply} style={{ flex: 1, background: '#34d399', borderColor: '#34d399' }}>Apply</Button>
                </div>
            </div>
        </div>
    );
};

export default DitherEditorPanel;
